import { useCallback, useEffect, useState } from 'react'
import { useManualQuery } from 'graphql-hooks'

import { getCompanyBillingInfoForDowngrade } from '../../graphql/custom-queries'
import * as logger from '../../services/logger'
import { IGetAddonsSubscriptionForBilling, IGetCompanyBillingInfoDataForDowngrade } from '../../types/company'
import type { IEntitySourceData } from '../plan-downgrade-modal/types'
import { checkCoreLimits, IAddonStatus } from './plan-boost-ending-mode'

// The query selects getAddonsSubscription.addons, but the shared billing type only declares automations
type IPlanBoostDowngradeQueryData = Omit<IGetCompanyBillingInfoDataForDowngrade, 'getAddonsSubscription'> & {
  getAddonsSubscription?: IGetAddonsSubscriptionForBilling & { addons?: IAddonStatus[] | null }
}

export interface IPlanBoostDowngradeData {
  sourceData: IEntitySourceData
  isOverCoreLimits: boolean
}

export interface IPlanBoostDowngradeDataResult {
  data: IPlanBoostDowngradeData | null
  reload: () => void
}

export const usePlanBoostDowngradeData = (enabled: boolean, featureFlags: string[]): IPlanBoostDowngradeDataResult => {
  const [getDowngradeInfoQuery] = useManualQuery<IPlanBoostDowngradeQueryData>(getCompanyBillingInfoForDowngrade)
  const [data, setData] = useState<IPlanBoostDowngradeData | null>(null)
  const [loadCount, setLoadCount] = useState(0)
  // Stale data is dropped first, so a failed reload hides the modal instead of offering already deleted entities again
  const reload = useCallback(() => {
    setData(null)
    setLoadCount((count) => count + 1)
  }, [])

  useEffect(() => {
    if (!enabled) return

    let isMounted = true
    const load = async () => {
      try {
        const response = await getDowngradeInfoQuery()
        if (!response.data || response.error) throw response.error
        const teams = response.data.getTeamList || []
        const locations = response.data.getLocationList || []
        const { allowUnlimitedTeams, allowUnlimitedLocations, isOverCoreLimits } = checkCoreLimits({
          teamCount: teams.length,
          locationCount: locations.length,
          featureFlags,
          addons: response.data.getAddonsSubscription?.addons ?? [],
        })
        if (!isMounted) return
        // PlanDowngradeModal caps at the plain Core limits, so an unlimited entity type is left out to keep it from being offered for deletion
        setData({
          isOverCoreLimits,
          sourceData: {
            teams: allowUnlimitedTeams ? [] : teams,
            locations: allowUnlimitedLocations ? [] : locations,
            leaveTypes: response.data.getLeaveTypeList || [],
          },
        })
      } catch (error) {
        logger.error('error fetching plan boost downgrade data', error)
      }
    }
    void load()

    return () => {
      isMounted = false
    }
  }, [enabled, featureFlags, loadCount])

  return { data, reload }
}
